import { ElementType, MouseEventHandler, ReactElement, ReactNode, useMemo, useState } from 'react';
import { styled } from '@mui/material';
import colors from '@o2/ui/theme/colors';
import { Stack, Tooltip } from '@o2/ui';
import { ContainerProps, default as Container } from '../Container';
import Divider from './Divider';
import Button, { ButtonProps } from './Button';
import { EyeDisabledFill, EyeFill, HomeFill } from '@o2/icons';
import Controls from './Controls';
import DropDownButton from './DropDownButton';
import DropDownItem, { DropdownItemProps } from './DropDownItem';
import UserButton from './UserButton';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system';
import palette from '@o2/ui/theme/palette';

export type AppEnvironment = 'production' | 'pre-production' | 'development';

export interface AppBarInternalElmIds {
  root: string;
  homeBtn: string;
  internalUserBtn: string;
  locationBtn: string;
  locationItem: string;
  roleBtn: string;
  roleItem: string;
  messageBtn: string;
  eyeBtn: string;
  contactPersonBtn: string;
  userButtonVerifiedStatus: string;
  selectContactPersonBtn: string;
  offerItem: string;
  offerItemRemoveBtn: string;
  salesBtn: string;
  omniBtn: string;
}

export interface AppBarInternalProps<
  TEyeButton extends ElementType = 'button',
  THomeButton extends ElementType = 'button'
> {
  children?: ReactNode;

  /**
   * Change bar color by environment
   */
  environment: AppEnvironment;

  /**
   * AppBarInternal includes a `@o2/common-business/Container`. You can set this container's `maxWidth`, or fall back to the
   * default 'full-wide'.
   */
  maxWidth?: ContainerProps['variant'];

  /*
   * Allows controlled mode, open state is usually provided from sharedObjets
   */
  initialOpen?: boolean;

  /**
   * Callback for handle show / hide internal content via Eye button
   */
  onToggleInternalContent?: (enabled: boolean) => void;

  /**
   * Eye button tooltip
   */
  eyeButtonTooltip?: ReactNode;

  /**
   * Props passed to the eye button (internal view toggle) (internally an AppBarInternal.Button itself).
   *
   * This prop has priority over `onToggleInternalContent` (if `onClick` is passed here as well, it will override the other one).
   */
  eyeButtonProps?: Partial<ButtonProps<TEyeButton>>;

  /**
   * Callback for handle click on Home button
   */
  onHomeButtonClick?: MouseEventHandler;

  /**
   * Home button tooltip content
   */
  homeButtonTooltip?: ReactNode;

  /**
   * Props passed to the Home button (internally an AppBarInternal.Button itself).
   *
   * This prop has priority over `onHomeButtonClick` (if `onClick` is passed here as well, it will override the other one).
   */
  homeButtonProps?: Partial<ButtonProps<THomeButton>>;

  /**
   * Element IDs. For values that are not passed, defaults will be used.
   *
   * Please note that you need to set IDs on all children of AppBarInternal (that you pass) yourself!
   *
   * @see {appBarInternalElmIds}
   */
  elmIds?: Partial<AppBarInternalElmIds>;
}

export const getEnvironmentColor = (env: AppBarInternalProps['environment']) => {
  switch (env) {
    case 'development':
      return colors.warning['700'];
    case 'pre-production':
      return colors.success['700'];
    default:
      return colors.internal['500'];
  }
};

const StyledBar = styled('div', {
  name: 'AppBarInternal',
  slot: 'root',
  shouldForwardProp: (propName) =>
    sysShouldForwardProp(propName) && propName !== 'isInternalContentHidden' && propName !== 'environment',
})<{ isInternalContentHidden?: boolean; environment: AppBarInternalProps['environment'] }>(
  ({ theme, isInternalContentHidden, environment }) => ({
    backgroundColor: isInternalContentHidden ? theme.palette.common.white : getEnvironmentColor(environment),
    height: 'auto',
    color: theme.palette.common.white,
    padding: 0,
    [theme.breakpoints.down('md')]: {
      height: 'auto',
    },
  })
);

const Wrapper = styled('div', {
  name: 'AppBarInternal',
  slot: 'Wrapper',
})(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'space-between',
  flexDirection: 'row',
  alignItems: 'center',
  height: 'auto',
  minHeight: 32,
  flexWrap: 'wrap',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    height: 'auto',
    alignItems: 'flex-start',
  },
}));

const EyeButtonWrapper = styled('div', {
  name: 'AppBarInternal',
  slot: 'EyeButtonWrapper',
  shouldForwardProp: (propName) => sysShouldForwardProp(propName) && propName !== 'environment',
})<{ environment: AppBarInternalProps['environment'] }>(({ environment }) => ({
  backgroundColor: getEnvironmentColor(environment),
}));

const StyledStack = styled(Stack, {
  name: 'AppBarInternal',
  slot: 'stack',
})(({ theme }) => ({
  '& > button:first-of-type, & > button:last-of-type': {
    marginLeft: theme.spacing(-1.5),
  },
  '& > div:last-of-type': {
    marginRight: theme.spacing(-1.5),
  },
}));

const appBarInternalTemplateId = '11.1.4';

export const appBarInternalElmIds: AppBarInternalElmIds = {
  root: appBarInternalTemplateId,
  roleBtn: `${appBarInternalTemplateId}_029`,
  roleItem: `${appBarInternalTemplateId}_029b`,
  contactPersonBtn: `${appBarInternalTemplateId}_022`,
  userButtonVerifiedStatus: `${appBarInternalTemplateId}_008`,
  selectContactPersonBtn: `${appBarInternalTemplateId}_021`,
  eyeBtn: `${appBarInternalTemplateId}_006`,
  homeBtn: `${appBarInternalTemplateId}_005`,
  internalUserBtn: `${appBarInternalTemplateId}_007`,
  locationBtn: `${appBarInternalTemplateId}_025`,
  locationItem: `${appBarInternalTemplateId}_025b`,
  messageBtn: `${appBarInternalTemplateId}_002`,
  offerItem: `${appBarInternalTemplateId}_030`,
  offerItemRemoveBtn: `${appBarInternalTemplateId}_031`,
  salesBtn: `${appBarInternalTemplateId}_038`,
  omniBtn: `${appBarInternalTemplateId}_039`,
};

const emptyObj = {};

/**
 * AppBar for internal users. Be sure that you have the environment set up correctly (production, pre-production, development)
 * and that the bar is only visible to internal users. **This logic is not implemented!**
 *
 * ### Base structure
 *
 * ```tsx
 * <AppBarInternal environment="production" onToggleInternalContent={(hidden) => alert(`Hide internal content: ${hidden}`)}>
 *   <AppBarInternal.Controls rightDivider>
 *     <AppBarInternal.Button>Button 1</AppBarInternal.Button>
 *     <AppBarInternal.Button>Button 2</AppBarInternal.Button>
 *     <AppBarInternal.DropDownButton label="Menu">
 *       <AppBarInternal.DropDownItem>Item 1</AppBarInternal.DropDownItem>
 *       <AppBarInternal.DropDownItem>Item 2</AppBarInternal.DropDownItem>
 *       <AppBarInternal.DropDownItem>Item 3</AppBarInternal.DropDownItem>
 *       <AppBarInternal.DropDownItem>Item 4</AppBarInternal.DropDownItem>
 *     </AppBarInternal.DropDownButton>
 *   </AppBarInternal.Controls leftDivider>
 *   <AppBarInternal.Controls>
 *     <AppBarInternal.UserButton verified={false} open={false} onClick={() => {alert("Open user menu")}>
 *       František Mžourek
 *     </AppBarInternal.UserButton>
 *   </AppBarInternal.Controls>
 * </AppBarInternal>
 * ```
 */
const AppBarInternal = <TEyeButton extends ElementType = 'button', THomeButton extends ElementType = 'button'>({
  children,
  environment,
  maxWidth = 'full-wide',
  onHomeButtonClick,
  homeButtonProps,
  homeButtonTooltip,
  onToggleInternalContent,
  eyeButtonTooltip,
  eyeButtonProps,
  initialOpen = false,
  elmIds: elmIdsProp = emptyObj,
  ...props
}: AppBarInternalProps<TEyeButton, THomeButton>) => {
  const [internalContentHidden, setInternalContentHidden] = useState(!initialOpen);
  const handleChangeDisplayInternalContent: MouseEventHandler = () => {
    setInternalContentHidden(!internalContentHidden);
    onToggleInternalContent && onToggleInternalContent(!internalContentHidden);
  };

  const elmIds = useMemo(
    () => ({
      ...appBarInternalElmIds,
      ...elmIdsProp,
    }),
    [elmIdsProp]
  );

  const HomeButton = (
    <Button data-elm={elmIds?.homeBtn} onClick={onHomeButtonClick} {...(homeButtonProps as ButtonProps)}>
      <HomeFill />
    </Button>
  );

  const EyeButton = (
    <Button data-elm={elmIds?.eyeBtn} onClick={handleChangeDisplayInternalContent} {...(eyeButtonProps as ButtonProps)}>
      {internalContentHidden ? <EyeDisabledFill /> : <EyeFill />}
    </Button>
  );

  return (
    <StyledBar isInternalContentHidden={internalContentHidden} environment={environment} {...props}>
      <Container disableMarginY variant={maxWidth}>
        <StyledStack direction="row" justifyContent={internalContentHidden ? 'end' : 'start'} alignItems="center">
          {!internalContentHidden && (
            <>
              {homeButtonTooltip ? <Tooltip title={homeButtonTooltip}>{HomeButton}</Tooltip> : <>{HomeButton}</>}
              {children && <Divider />}
              <Wrapper>{children}</Wrapper>
              {children && <Divider />}
            </>
          )}
          <EyeButtonWrapper environment={environment}>
            {eyeButtonTooltip ? <Tooltip title={eyeButtonTooltip}>{EyeButton}</Tooltip> : <>{EyeButton}</>}
          </EyeButtonWrapper>
        </StyledStack>
      </Container>
    </StyledBar>
  );
};

AppBarInternal.Button = Button;
AppBarInternal.UserButton = UserButton;
AppBarInternal.DropDownButton = DropDownButton;
AppBarInternal.DropDownItem = DropDownItem as <T extends ElementType = 'button'>(
  props: DropdownItemProps<T>
) => ReactElement;
AppBarInternal.Controls = Controls;

export default AppBarInternal;
